import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';
import type React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { authManager } from '../../utils/auth/auth-manager';
import { clearSessionImpersonatedUserId, writeImpersonatedUserIdToStorage } from '../../utils/impersonation-storage';

// Helper function to log user information in a nice format
const logUserInfo = (user: User | null, context: string) => {
    if (!user) {
        console.log(`🔐 [Auth] ${context}: No user authenticated`);
        return;
    }

    const userInfo = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified,
        isAnonymous: user.isAnonymous,
        providerData: user.providerData.map(provider => ({
            providerId: provider.providerId,
            uid: provider.uid,
            email: provider.email,
            displayName: provider.displayName,
        })),
        metadata: {
            creationTime: user.metadata.creationTime,
            lastSignInTime: user.metadata.lastSignInTime,
        },
        tenantId: user.tenantId,
    };

    console.group(`🔐 [Auth] ${context}`);
    console.log('📋 User Info:', userInfo);
    // console.log('🔗 Raw Firebase User:', user);
    console.groupEnd();
};

// Helper function to log authentication events
const logAuthEvent = (event: string, details?: unknown) => {
    console.log(`[Auth Event] ${event}`, details || '');
};

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: Error | null;
    login: () => Promise<void>;
    logout: () => Promise<void>;
}
// Create the auth context with default values

export const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    error: null,
    login: async () => {},
    logout: async () => {},
});

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

interface AuthProviderProps {
    children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const hasAttemptedDevLoginRef = useRef(false);

    // Make user info accessible from browser console
    useEffect(() => {
        (window as typeof window & { logCurrentUser?: () => void }).logCurrentUser = () => {
            const currentUser = getAuth().currentUser;
            logUserInfo(currentUser, 'Manual User Info Request');
            if (currentUser) {
                authManager
                    .fetchUserData(currentUser)
                    .then(userData => {
                        console.group('🔐 [Auth] Enhanced User Data (Manual Request)');
                        console.log('📊 Extracted User Data:', userData);
                        console.groupEnd();
                    })
                    .catch(error => {
                        console.error('🔐 [Auth] Failed to fetch enhanced user data:', error);
                    });
            }
        };
    }, []);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
            if (!firebaseUser && import.meta.env.DEV && !hasAttemptedDevLoginRef.current) {
                hasAttemptedDevLoginRef.current = true;
                try {
                    logAuthEvent('RAM dev ADC login attempt started');
                    await authManager.logInWithDevCustomToken();
                    return;
                } catch (error) {
                    logAuthEvent('RAM dev ADC login unavailable', {
                        error: error instanceof Error ? error.message : 'Unknown error',
                    });
                }
            }

            setUser(firebaseUser);
            setLoading(false);

            // Log user info on state change
            logUserInfo(firebaseUser, 'Auth State Changed');

            // If user is authenticated, also fetch and log additional user data
            if (firebaseUser) {
                try {
                    const userData = await authManager.fetchUserData(firebaseUser);
                    console.group('🔐 [Auth] Enhanced User Data', userData);
                    // Get ID token to check if authentication token is available
                    // const idToken = await firebaseUser.getIdToken();
                    // console.log('🔑 Auth Token Available:', !!idToken);
                    console.groupEnd();
                } catch (error) {
                    setError(error as Error);
                    console.error('🔐 [Auth] Failed to fetch enhanced user data:', error);
                }
            }
        });

        return unsubscribe;
    }, []);

    const login = async () => {
        try {
            logAuthEvent('Login attempt started');
            const result = await authManager.logIn();
            logAuthEvent('Login successful', { providerId: result?.user?.providerData?.[0]?.providerId });
            return result;
        } catch (error) {
            setError(error as Error);
            logAuthEvent('Login failed', {
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            throw error;
        }
    };

    const logout = async () => {
        try {
            logAuthEvent('Logout attempt started');
            logUserInfo(user, 'User Before Logout');
            writeImpersonatedUserIdToStorage(null);
            clearSessionImpersonatedUserId();
            await authManager.logOut();
            logAuthEvent('Logout successful');
        } catch (error) {
            setError(error as Error);
            logAuthEvent('Logout failed', {
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            throw error;
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
